// The chart engine and the chart theme. This folder (chart/) is the only place that may import
// Highcharts: an ESLint rule blocks the import in every other folder. A screen draws a chart only
// with a chart Component, never with the engine.
// The theme turns the chart tokens into the base options of every chart: the font, the grid, the
// axes, the legend, the crosshair and the markers. A chart Component starts from chartTheme() and
// adds its series.
// Licence: Highcharts needs a paid licence for a commercial product. See www.highcharts.com/license.
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
// The order is important: the modules attach to the Highcharts of the line above.
import 'highcharts/highcharts-more';
import 'highcharts/modules/accessibility';
import { CHART_COLORS, CHART_FONT, CHART_MARKS } from './chartTokens';

export { Highcharts, HighchartsReact };

const textStyle = (color: string): Highcharts.CSSObject => ({
	color, fontSize: `${CHART_MARKS.fontSize}px`, fontWeight: 'normal',
});

// The base options of a chart of `height` px. A chart Component merges its own options on top.
export const chartTheme = (height: number): Highcharts.Options => ({
	chart: {
		height,
		backgroundColor: CHART_COLORS.surface,
		style: { fontFamily: CHART_FONT },
		spacing: [8, 4, 8, 0],
		animation: false,
	},
	title: { text: undefined },
	subtitle: { text: undefined },
	credits: { enabled: false },
	// Stay dates are UTC midnights. No time zone moves them.
	time: { timezone: 'UTC' },
	legend: {
		enabled: false,
		align: 'left',
		verticalAlign: 'top',
		margin: 12,
		padding: 0,
		itemDistance: 16,
		symbolWidth: 16,
		symbolHeight: 10,
		symbolRadius: 2,
		itemStyle: textStyle(CHART_COLORS.textSecondary),
		itemHoverStyle: { color: CHART_COLORS.textPrimary },
		itemHiddenStyle: { color: CHART_COLORS.axisLine, textDecoration: 'none' },
	},
	xAxis: {
		lineColor: CHART_COLORS.axisLine,
		lineWidth: 1,
		tickLength: 0,
		gridLineWidth: 0,
		labels: { style: textStyle(CHART_COLORS.axisText) },
		crosshair: { width: 1, color: CHART_COLORS.axisLine },
		title: { text: undefined },
	},
	yAxis: {
		gridLineColor: CHART_COLORS.grid,
		gridLineWidth: CHART_MARKS.gridWidth,
		gridLineDashStyle: 'Solid',
		lineWidth: 0,
		tickLength: 0,
		labels: { style: textStyle(CHART_COLORS.axisText) },
		title: { text: undefined },
	},
	tooltip: {
		shared: true,
		useHTML: true,
		// ROO2-262 (app-only change, user 2026-10-21; the design system must take it): the tooltip draws in its own layer on
		// the page, not inside the chart, whose container clips at the chart's edges: the tooltip of a low chart (one
		// stacked bar) was cut. zIndex 50 = the layer of the other tooltips (z-50), over drawers and dialogs (z-10).
		outside: true,
		backgroundColor: 'transparent',
		borderWidth: 0,
		shadow: false,
		padding: 0,
		hideDelay: 0,
		style: { fontSize: `${CHART_MARKS.fontSize}px`, zIndex: 50 },
	},
	plotOptions: {
		series: {
			animation: false,
			lineWidth: CHART_MARKS.lineWidth,
			connectNulls: false,
			marker: {
				enabled: false,
				// One shape for every line: the colour and the legend tell the lines apart.
				symbol: 'circle',
				radius: CHART_MARKS.markerRadius,
				lineWidth: CHART_MARKS.markerRing,
				lineColor: CHART_COLORS.surface,
				states: { hover: { radiusPlus: 0, lineWidthPlus: 0 } },
			},
			states: {
				hover: { lineWidthPlus: 0, halo: { size: 0 } },
				// A shared tooltip shows every line, so no line fades on a hover.
				inactive: { enabled: false },
			},
		},
	},
	accessibility: {
		enabled: true,
	},
});
